import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './ReviewManagement.css';

interface ReviewManagementProps {
  storeId: string;
  storeName: string;
}

type ReviewSection = 'overview' | 'reviews' | 'solicitation' | 'settings';
type ReviewFilter = 'all' | 'pending_ai' | 'ai_generated' | 'approved' | 'posted';

interface ReviewStats {
  averageRating: number;
  totalReviews: number;
  responseRate: number;
  aiResponsesGenerated: number;
  pendingReplies: number;
  ratingDistribution: { [key: number]: number };
  sentimentBreakdown: {
    positive: number;
    mixed: number;
    negative: number;
  };
}

interface Review {
  reviewId: string;
  reviewerName: string;
  rating: number;
  date: string;
  platform: 'google' | 'yelp' | 'facebook';
  text: string;
  replyStatus: 'pending_ai' | 'ai_generated' | 'approved' | 'posted';
  replyText?: string;
  postedReplyText?: string;
  sentiment?: string;
  externalReviewId?: string;
  mentionedItems?: string[];
}

interface RequestStats {
  totalRequests: number;
  sent: number;
  completionRate: number;
  averageSurveyRating: number;
  channelBreakdown: {
    sms: number;
    email: number;
  };
  routingBreakdown: {
    google: number;
    privateFeedback: number;
  };
}

interface ReviewRequest {
  requestId: string;
  customerName: string;
  channel: 'sms' | 'email';
  status: 'pending' | 'sent' | 'completed' | 'opted_out';
  sentAt: string;
  completedAt?: string;
  surveyRating?: number;
  routedTo?: 'google' | 'private';
}

interface ReviewConfig {
  autoResponseMode: 'automatic' | 'approval_required';
  googleBusinessUrl: string;
  yelpBusinessUrl: string;
  brandVoice: {
    tone: string;
    style: string;
    signOffName: string;
  };
  timingOverrides: {
    dineInDelay: number;
    takeoutDelay: number;
    deliveryDelay: number;
  };
  enableSms: boolean;
  enableEmail: boolean;
}

export const ReviewManagement: React.FC<ReviewManagementProps> = ({ storeId, storeName }) => {
  const [activeSection, setActiveSection] = useState<ReviewSection>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Overview state
  const [stats, setStats] = useState<ReviewStats>({
    averageRating: 0,
    totalReviews: 0,
    responseRate: 0,
    aiResponsesGenerated: 0,
    pendingReplies: 0,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    sentimentBreakdown: { positive: 0, mixed: 0, negative: 0 }
  });

  // Reviews state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const [generatingReplyId, setGeneratingReplyId] = useState<string | null>(null);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [postingReplyId, setPostingReplyId] = useState<string | null>(null);
  const [approvingReplyId, setApprovingReplyId] = useState<string | null>(null);

  // Solicitation state
  const [requestStats, setRequestStats] = useState<RequestStats>({
    totalRequests: 0,
    sent: 0,
    completionRate: 0,
    averageSurveyRating: 0,
    channelBreakdown: { sms: 0, email: 0 },
    routingBreakdown: { google: 0, privateFeedback: 0 }
  });
  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([]);

  // Settings state
  const [config, setConfig] = useState<ReviewConfig>({
    autoResponseMode: 'approval_required',
    googleBusinessUrl: '',
    yelpBusinessUrl: '',
    brandVoice: {
      tone: 'professional',
      style: 'conversational',
      signOffName: ''
    },
    timingOverrides: {
      dineInDelay: 120,
      takeoutDelay: 30,
      deliveryDelay: 60
    },
    enableSms: true,
    enableEmail: true
  });
  const [savingConfig, setSavingConfig] = useState(false);

  const fetchOverviewData = useCallback(async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.reviewStats(storeId));
      if (response.ok) {
        const data = await response.json();
        const statsData = data.data || data;
        setStats({
          averageRating: statsData.averageRating ?? 0,
          totalReviews: statsData.totalReviews ?? 0,
          responseRate: statsData.responseRate ?? 0,
          aiResponsesGenerated: statsData.aiResponsesGenerated ?? 0,
          pendingReplies: statsData.pendingReplies ?? 0,
          ratingDistribution: statsData.ratingDistribution ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          sentimentBreakdown: statsData.sentimentBreakdown ?? { positive: 0, mixed: 0, negative: 0 }
        });
      }
    } catch (err) {
      console.error('[ReviewManagement] Failed to fetch stats:', err);
    }
  }, [storeId]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.reviews(storeId));
      if (response.ok) {
        const data = await response.json();
        setReviews(data.data || data.reviews || []);
      }
    } catch (err) {
      console.error('[ReviewManagement] Failed to fetch reviews:', err);
    }
  }, [storeId]);

  const fetchSolicitationData = useCallback(async () => {
    try {
      const [statsResponse, requestsResponse] = await Promise.all([
        authenticatedFetch(API_ENDPOINTS.reviewRequestStats(storeId)),
        authenticatedFetch(API_ENDPOINTS.reviewRequests(storeId))
      ]);

      if (statsResponse.ok) {
        const data = await statsResponse.json();
        const reqStats = data.data || data;
        setRequestStats({
          totalRequests: reqStats.totalRequests ?? 0,
          sent: reqStats.sent ?? 0,
          completionRate: reqStats.completionRate ?? 0,
          averageSurveyRating: reqStats.averageSurveyRating ?? 0,
          channelBreakdown: reqStats.channelBreakdown ?? { sms: 0, email: 0 },
          routingBreakdown: reqStats.routingBreakdown ?? { google: 0, privateFeedback: 0 }
        });
      }

      if (requestsResponse.ok) {
        const data = await requestsResponse.json();
        setReviewRequests(data.data || data.requests || []);
      }
    } catch (err) {
      console.error('[ReviewManagement] Failed to fetch solicitation data:', err);
    }
  }, [storeId]);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.reviewConfig(storeId));
      if (response.ok) {
        const data = await response.json();
        const configData = data.data || data;
        setConfig(prev => ({
          autoResponseMode: configData.autoResponseMode ?? prev.autoResponseMode,
          googleBusinessUrl: configData.googleBusinessUrl ?? prev.googleBusinessUrl,
          yelpBusinessUrl: configData.yelpBusinessUrl ?? prev.yelpBusinessUrl,
          brandVoice: {
            tone: configData.brandVoice?.tone ?? prev.brandVoice.tone,
            style: configData.brandVoice?.style ?? prev.brandVoice.style,
            signOffName: configData.brandVoice?.signOffName ?? prev.brandVoice.signOffName
          },
          timingOverrides: {
            dineInDelay: configData.timingOverrides?.dineInDelay ?? prev.timingOverrides.dineInDelay,
            takeoutDelay: configData.timingOverrides?.takeoutDelay ?? prev.timingOverrides.takeoutDelay,
            deliveryDelay: configData.timingOverrides?.deliveryDelay ?? prev.timingOverrides.deliveryDelay
          },
          enableSms: configData.enableSms ?? prev.enableSms,
          enableEmail: configData.enableEmail ?? prev.enableEmail
        }));
      }
    } catch (err) {
      console.error('[ReviewManagement] Failed to fetch config:', err);
    }
  }, [storeId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          fetchOverviewData(),
          fetchReviews(),
          fetchSolicitationData(),
          fetchConfig()
        ]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load review data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [storeId, fetchOverviewData, fetchReviews, fetchSolicitationData, fetchConfig]);

  const handleGenerateReply = async (reviewId: string) => {
    setGeneratingReplyId(reviewId);
    setError(null);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.reviewGenerateReply(reviewId), {
        method: 'POST',
        body: JSON.stringify({ storeId })
      });

      if (response.ok) {
        const data = await response.json();
        const generatedReply = data.data?.replyText || data.replyText || '';
        setReviews(prev =>
          prev.map(r =>
            r.reviewId === reviewId
              ? { ...r, replyStatus: 'ai_generated' as const, replyText: generatedReply }
              : r
          )
        );
        setSuccess('AI reply generated successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to generate AI reply');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate reply');
    } finally {
      setGeneratingReplyId(null);
    }
  };

  const handleApproveReply = async (reviewId: string) => {
    setApprovingReplyId(reviewId);
    setError(null);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.reviewApprove(reviewId), {
        method: 'PUT'
      });

      if (response.ok) {
        setReviews(prev =>
          prev.map(r =>
            r.reviewId === reviewId
              ? { ...r, replyStatus: 'approved' as const }
              : r
          )
        );
        setSuccess('Reply approved');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Failed to approve reply');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve reply');
    } finally {
      setApprovingReplyId(null);
    }
  };

  const handleEditReply = async (reviewId: string) => {
    if (!editReplyText.trim()) return;
    setError(null);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.reviewEditReply(reviewId), {
        method: 'PUT',
        body: JSON.stringify({ replyText: editReplyText })
      });

      if (response.ok) {
        setReviews(prev =>
          prev.map(r =>
            r.reviewId === reviewId
              ? { ...r, replyText: editReplyText }
              : r
          )
        );
        setEditingReplyId(null);
        setEditReplyText('');
        setSuccess('Reply updated');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Failed to update reply');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update reply');
    }
  };

  const handlePostReply = async (reviewId: string) => {
    setPostingReplyId(reviewId);
    setError(null);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.reviewPostReply(reviewId), {
        method: 'POST',
        body: JSON.stringify({ storeId })
      });

      if (response.ok) {
        setReviews(prev =>
          prev.map(r =>
            r.reviewId === reviewId
              ? { ...r, replyStatus: 'posted' as const, postedReplyText: r.replyText }
              : r
          )
        );
        setSuccess('Reply posted to platform');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Failed to post reply');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post reply');
    } finally {
      setPostingReplyId(null);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    setError(null);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.reviewConfig(storeId), {
        method: 'PUT',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setSuccess('Settings saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSavingConfig(false);
    }
  };

  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'google':
        return (
          <svg className="rc-platform-logo" viewBox="0 0 24 24" width="28" height="28">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        );
      case 'yelp':
        return (
          <svg className="rc-platform-logo" viewBox="0 0 24 24" width="28" height="28">
            <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.768-4.076c.564-.83 1.865-.394 1.865.625v3.024c0 .244-.17.542-.462.624zm-7.166 5.1l.93-5.166c.17-.96 1.527-1.08 1.866-.17l1.66 4.47c.34.91-.627 1.78-1.386 1.25l-2.59-1.834c-.21-.148-.387-.34-.48-.55zm-2.498.04l-2.59 1.835c-.76.53-1.726-.34-1.387-1.25l1.66-4.47c.34-.91 1.697-.79 1.867.17l.93 5.166c.043.24-.033.488-.24.66-.053.04-.133.06-.24-.11zm-3.33-5.74l-4.994-1.434c-.96-.276-1.026-1.627-.096-2.002l4.56-1.84c.93-.375 1.81.63 1.247 1.46l-2.768 4.076c-.14.21-.37.374-.627.374-.128 0-.22-.035-.323-.103v-.53zM12.72 2.46l-1.62 9.04c-.17.96-1.527.96-1.697 0L7.78 2.46C7.61 1.5 8.57.634 9.4 1.14l2.42 1.592c.34.228.56.6.6 1.006l.3 1.72v-3z" fill="#D32323"/>
          </svg>
        );
      case 'facebook':
        return (
          <svg className="rc-platform-logo" viewBox="0 0 24 24" width="28" height="28">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
          </svg>
        );
      default:
        return <i className="fas fa-star rc-platform-logo-fallback"></i>;
    }
  };

  const getReviewUrl = (review: Review): string | null => {
    if (review.platform === 'yelp' && config.yelpBusinessUrl) {
      return config.yelpBusinessUrl;
    }
    if (review.platform === 'google' && config.googleBusinessUrl) {
      return config.googleBusinessUrl;
    }
    return null;
  };

  const getSentimentColor = (sentiment?: string): string => {
    switch (sentiment) {
      case 'positive': return '#10b981';
      case 'negative': return '#ef4444';
      case 'mixed': return '#f59e0b';
      default: return '#9ca3af';
    }
  };

  const renderStars = (rating: number, maxStars: number = 5) => {
    const stars = [];
    for (let i = 1; i <= maxStars; i++) {
      if (i <= Math.floor(rating)) {
        stars.push(<i key={i} className="fas fa-star star-filled"></i>);
      } else if (i - rating < 1 && i - rating > 0) {
        stars.push(<i key={i} className="fas fa-star-half-alt star-filled"></i>);
      } else {
        stars.push(<i key={i} className="far fa-star star-empty"></i>);
      }
    }
    return <span className="star-rating">{stars}</span>;
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending_ai': return 'Pending';
      case 'ai_generated': return 'AI Generated';
      case 'approved': return 'Approved';
      case 'posted': return 'Posted';
      default: return status;
    }
  };

  const getRequestStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'sent': return 'Sent';
      case 'completed': return 'Completed';
      case 'opted_out': return 'Opted Out';
      default: return status;
    }
  };

  const filteredReviews = reviews.filter(r =>
    reviewFilter === 'all' ? true : r.replyStatus === reviewFilter
  );

  const maxRatingCount = Math.max(...Object.values(stats.ratingDistribution), 1);

  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  // -- RENDER SECTIONS --

  const renderOverview = () => (
    <div className="review-overview-section">
      <div className="review-stats-grid">
        <div className="review-stat-card rating-card">
          <div className="review-stat-icon">
            <i className="fas fa-star"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">{stats.averageRating.toFixed(1)}</div>
            <div className="review-stat-label">Average Rating</div>
            <div className="review-stat-stars">{renderStars(stats.averageRating)}</div>
          </div>
        </div>

        <div className="review-stat-card total-card">
          <div className="review-stat-icon">
            <i className="fas fa-comments"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">{stats.totalReviews}</div>
            <div className="review-stat-label">Total Reviews</div>
          </div>
        </div>

        <div className="review-stat-card response-card">
          <div className="review-stat-icon">
            <i className="fas fa-reply"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">{stats.responseRate}%</div>
            <div className="review-stat-label">Response Rate</div>
          </div>
        </div>

        <div className="review-stat-card ai-card">
          <div className="review-stat-icon">
            <i className="fas fa-robot"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">{stats.aiResponsesGenerated}</div>
            <div className="review-stat-label">AI Responses Generated</div>
          </div>
        </div>

        <div className="review-stat-card pending-card">
          <div className="review-stat-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">{stats.pendingReplies}</div>
            <div className="review-stat-label">Pending Replies</div>
          </div>
        </div>
      </div>

      <div className="review-overview-panels">
        <div className="review-panel">
          <h4>Rating Distribution</h4>
          <div className="rating-distribution">
            {[5, 4, 3, 2, 1].map(star => (
              <div key={star} className="rating-bar-row">
                <span className="rating-bar-label">{star} star{star !== 1 ? 's' : ''}</span>
                <div className="rating-bar-track">
                  <div
                    className="rating-bar-fill"
                    style={{
                      width: `${(stats.ratingDistribution[star] / maxRatingCount) * 100}%`
                    }}
                  ></div>
                </div>
                <span className="rating-bar-count">{stats.ratingDistribution[star]}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="review-panel">
          <h4>Sentiment Breakdown</h4>
          <div className="sentiment-breakdown">
            <div className="sentiment-item positive">
              <div className="sentiment-icon">
                <i className="fas fa-smile"></i>
              </div>
              <div className="sentiment-info">
                <div className="sentiment-count">{stats.sentimentBreakdown.positive}</div>
                <div className="sentiment-label">Positive</div>
              </div>
            </div>
            <div className="sentiment-item mixed">
              <div className="sentiment-icon">
                <i className="fas fa-meh"></i>
              </div>
              <div className="sentiment-info">
                <div className="sentiment-count">{stats.sentimentBreakdown.mixed}</div>
                <div className="sentiment-label">Mixed</div>
              </div>
            </div>
            <div className="sentiment-item negative">
              <div className="sentiment-icon">
                <i className="fas fa-frown"></i>
              </div>
              <div className="sentiment-info">
                <div className="sentiment-count">{stats.sentimentBreakdown.negative}</div>
                <div className="sentiment-label">Negative</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderReviews = () => (
    <div className="review-reviews-section">
      <div className="review-filter-bar">
        {(['all', 'pending_ai', 'ai_generated', 'approved', 'posted'] as ReviewFilter[]).map(filter => (
          <button
            key={filter}
            className={`review-filter-btn ${reviewFilter === filter ? 'active' : ''}`}
            onClick={() => setReviewFilter(filter)}
          >
            {filter === 'all' ? 'All' :
             filter === 'pending_ai' ? 'Pending' :
             filter === 'ai_generated' ? 'AI Generated' :
             filter === 'approved' ? 'Approved' : 'Posted'}
            {filter !== 'all' && (
              <span className="filter-count">
                {reviews.filter(r => r.replyStatus === filter).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="review-list">
        {filteredReviews.length === 0 ? (
          <div className="review-empty">
            <i className="fas fa-inbox"></i>
            <p>No reviews match the selected filter.</p>
          </div>
        ) : (
          filteredReviews.map(review => {
            const reviewUrl = getReviewUrl(review);
            return (
            <div key={review.reviewId} className={`rc-card rc-card--${review.platform}`}>
              <div className="rc-card__top">
                <div className="rc-card__logo-col">
                  {getPlatformLogo(review.platform)}
                </div>
                <div className="rc-card__stars">
                  {renderStars(review.rating)}
                </div>
                <div className="rc-card__links">
                  {reviewUrl && (
                    <a href={reviewUrl} target="_blank" rel="noopener noreferrer" className="rc-card__link" title={`View on ${review.platform}`}>
                      <i className="fas fa-external-link-alt"></i>
                      <span>Live</span>
                    </a>
                  )}
                  {review.rating <= 2 && (
                    <button className="rc-card__link rc-card__link--recovery" title="Open Recovery Editor">
                      <i className="fas fa-first-aid"></i>
                      <span>Recover</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="rc-card__body">
                <div className="rc-card__reviewer-row">
                  <span className="rc-card__reviewer">{review.reviewerName}</span>
                  <span className="rc-card__date">{formatDate(review.date)}</span>
                </div>
                <div className="rc-card__text">{review.text}</div>
                {review.mentionedItems && review.mentionedItems.length > 0 && (
                  <div className="rc-card__mentions">
                    {review.mentionedItems.map(item => (
                      <span key={item} className="rc-card__mention-tag">{item}</span>
                    ))}
                  </div>
                )}
              </div>

              <div className="rc-card__footer">
                <div className="rc-card__footer-left">
                  <span className={`rc-card__status rc-card__status--${review.replyStatus}`}>
                    {getStatusLabel(review.replyStatus)}
                  </span>
                  {review.sentiment && (
                    <span className="rc-card__sentiment" style={{ color: getSentimentColor(review.sentiment) }}>
                      <i className={`fas fa-${review.sentiment === 'positive' ? 'smile' : review.sentiment === 'negative' ? 'frown' : 'meh'}`}></i>
                      {review.sentiment}
                    </span>
                  )}
                </div>
                <span className={`rc-card__platform-label rc-card__platform-label--${review.platform}`}>
                  {review.platform === 'google' ? 'Google' : review.platform === 'yelp' ? 'Yelp' : 'Facebook'}
                </span>
              </div>

              <div className="review-reply-section">
                {review.replyStatus === 'pending_ai' && (
                  <button
                    className="review-action-btn generate-btn"
                    onClick={() => handleGenerateReply(review.reviewId)}
                    disabled={generatingReplyId === review.reviewId}
                  >
                    {generatingReplyId === review.reviewId ? (
                      <><i className="fas fa-spinner fa-spin"></i> Generating...</>
                    ) : (
                      <><i className="fas fa-robot"></i> Generate AI Reply</>
                    )}
                  </button>
                )}

                {review.replyStatus === 'ai_generated' && (
                  <div className="review-reply-content">
                    <div className="reply-header">
                      <span className="reply-label">AI Generated Reply</span>
                    </div>
                    {editingReplyId === review.reviewId ? (
                      <div className="reply-edit-area">
                        <textarea className="reply-textarea" value={editReplyText} onChange={(e) => setEditReplyText(e.target.value)} rows={4} />
                        <div className="reply-edit-actions">
                          <button className="review-action-btn save-edit-btn" onClick={() => handleEditReply(review.reviewId)}>
                            <i className="fas fa-check"></i> Save
                          </button>
                          <button className="review-action-btn cancel-edit-btn" onClick={() => { setEditingReplyId(null); setEditReplyText(''); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="reply-text">{review.replyText}</div>
                        <div className="reply-actions">
                          <button className="review-action-btn approve-btn" onClick={() => handleApproveReply(review.reviewId)} disabled={approvingReplyId === review.reviewId}>
                            {approvingReplyId === review.reviewId ? <><i className="fas fa-spinner fa-spin"></i> Approving...</> : <><i className="fas fa-check-circle"></i> Approve</>}
                          </button>
                          <button className="review-action-btn edit-btn" onClick={() => { setEditingReplyId(review.reviewId); setEditReplyText(review.replyText || ''); }}>
                            <i className="fas fa-pen"></i> Edit
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {review.replyStatus === 'approved' && (
                  <div className="review-reply-content">
                    <div className="reply-header"><span className="reply-label">Approved Reply</span></div>
                    <div className="reply-text">{review.replyText}</div>
                    <div className="reply-actions">
                      <button className="review-action-btn post-btn" onClick={() => handlePostReply(review.reviewId)} disabled={postingReplyId === review.reviewId}>
                        {postingReplyId === review.reviewId ? <><i className="fas fa-spinner fa-spin"></i> Posting...</> : <><i className="fas fa-paper-plane"></i> Post Reply</>}
                      </button>
                    </div>
                  </div>
                )}

                {review.replyStatus === 'posted' && (
                  <div className="review-reply-content posted-reply">
                    <div className="reply-header">
                      <span className="reply-label">Posted Reply</span>
                      <span className="posted-indicator"><i className="fas fa-check-double"></i> Live</span>
                    </div>
                    <div className="reply-text">{review.postedReplyText || review.replyText}</div>
                  </div>
                )}
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );

  const renderSolicitation = () => (
    <div className="review-solicitation-section">
      <div className="review-stats-grid solicitation-stats">
        <div className="review-stat-card">
          <div className="review-stat-icon">
            <i className="fas fa-paper-plane"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">{requestStats.totalRequests}</div>
            <div className="review-stat-label">Total Requests</div>
          </div>
        </div>

        <div className="review-stat-card">
          <div className="review-stat-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">{requestStats.sent}</div>
            <div className="review-stat-label">Sent</div>
          </div>
        </div>

        <div className="review-stat-card">
          <div className="review-stat-icon">
            <i className="fas fa-percentage"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">{requestStats.completionRate}%</div>
            <div className="review-stat-label">Completion Rate</div>
          </div>
        </div>

        <div className="review-stat-card">
          <div className="review-stat-icon">
            <i className="fas fa-star"></i>
          </div>
          <div className="review-stat-content">
            <div className="review-stat-value">
              {requestStats.averageSurveyRating > 0
                ? requestStats.averageSurveyRating.toFixed(1)
                : '--'}
            </div>
            <div className="review-stat-label">Avg. Survey Rating</div>
          </div>
        </div>
      </div>

      <div className="review-overview-panels">
        <div className="review-panel">
          <h4>Channel Breakdown</h4>
          <div className="channel-breakdown">
            <div className="channel-item">
              <div className="channel-icon sms">
                <i className="fas fa-sms"></i>
              </div>
              <div className="channel-info">
                <div className="channel-count">{requestStats.channelBreakdown.sms}</div>
                <div className="channel-label">SMS</div>
              </div>
            </div>
            <div className="channel-item">
              <div className="channel-icon email">
                <i className="fas fa-envelope"></i>
              </div>
              <div className="channel-info">
                <div className="channel-count">{requestStats.channelBreakdown.email}</div>
                <div className="channel-label">Email</div>
              </div>
            </div>
          </div>
        </div>

        <div className="review-panel">
          <h4>Feedback Routing</h4>
          <div className="channel-breakdown">
            <div className="channel-item">
              <div className="channel-icon google">
                <i className="fab fa-google"></i>
              </div>
              <div className="channel-info">
                <div className="channel-count">{requestStats.routingBreakdown.google}</div>
                <div className="channel-label">Google Reviews</div>
              </div>
            </div>
            <div className="channel-item">
              <div className="channel-icon private">
                <i className="fas fa-lock"></i>
              </div>
              <div className="channel-info">
                <div className="channel-count">{requestStats.routingBreakdown.privateFeedback}</div>
                <div className="channel-label">Private Feedback</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="review-panel request-timeline-panel">
        <h4>Recent Activity</h4>
        <div className="request-timeline">
          {reviewRequests.length === 0 ? (
            <div className="review-empty">
              <p>No review requests yet.</p>
            </div>
          ) : (
            reviewRequests.slice(0, 20).map(request => (
              <div key={request.requestId} className="request-timeline-item">
                <div className="timeline-dot">
                  <i className={
                    request.status === 'completed' ? 'fas fa-check' :
                    request.status === 'sent' ? 'fas fa-paper-plane' :
                    request.status === 'opted_out' ? 'fas fa-ban' :
                    'fas fa-clock'
                  }></i>
                </div>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-name">{request.customerName}</span>
                    <span className={`status-badge status-${request.status}`}>
                      {getRequestStatusLabel(request.status)}
                    </span>
                  </div>
                  <div className="timeline-details">
                    <span className="timeline-channel">
                      <i className={request.channel === 'sms' ? 'fas fa-sms' : 'fas fa-envelope'}></i>
                      {request.channel.toUpperCase()}
                    </span>
                    {request.surveyRating !== undefined && request.surveyRating > 0 && (
                      <span className="timeline-survey-rating">
                        {renderStars(request.surveyRating)}
                      </span>
                    )}
                    {request.routedTo && (
                      <span className="timeline-routed">
                        <i className={request.routedTo === 'google' ? 'fab fa-google' : 'fas fa-lock'}></i>
                        {request.routedTo === 'google' ? 'Google' : 'Private'}
                      </span>
                    )}
                  </div>
                  <div className="timeline-time">
                    {formatDateTime(request.sentAt || request.completedAt || '')}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="review-settings-section">
      <div className="review-settings-form">
        <div className="settings-group">
          <h4>Auto Response Mode</h4>
          <p className="settings-description">
            Choose whether AI-generated replies are posted automatically or require manual approval before publishing.
          </p>
          <div className="response-mode-toggle">
            <button
              className={`mode-btn ${config.autoResponseMode === 'automatic' ? 'active' : ''}`}
              onClick={() => setConfig(prev => ({ ...prev, autoResponseMode: 'automatic' }))}
            >
              <i className="fas fa-bolt"></i>
              Automatic
            </button>
            <button
              className={`mode-btn ${config.autoResponseMode === 'approval_required' ? 'active' : ''}`}
              onClick={() => setConfig(prev => ({ ...prev, autoResponseMode: 'approval_required' }))}
            >
              <i className="fas fa-user-check"></i>
              Approval Required
            </button>
          </div>
          <div className="mode-description">
            {config.autoResponseMode === 'automatic'
              ? 'AI replies will be generated and posted automatically without manual review.'
              : 'AI replies will be generated but held for your review before posting.'}
          </div>
        </div>

        <div className="settings-group">
          <h4>Review Platforms</h4>
          <p className="settings-description">
            Connect your review platforms to enable monitoring and response management.
          </p>

          <div className="settings-field">
            <label htmlFor="yelp-url">Yelp Business Page URL</label>
            <input
              id="yelp-url"
              type="url"
              placeholder="https://www.yelp.com/biz/your-restaurant"
              value={config.yelpBusinessUrl}
              onChange={(e) => setConfig(prev => ({ ...prev, yelpBusinessUrl: e.target.value }))}
            />
            <p className="settings-field-help">
              Paste your Yelp business page URL. This enables Yelp review monitoring.
            </p>
          </div>

          <div className="platform-status-row">
            <div className="platform-status-item">
              <i className="fab fa-google" style={{ color: '#4285f4' }}></i>
              <span>Google Business</span>
              <span className="platform-status-badge">Managed in Google tab</span>
            </div>
          </div>
        </div>

        <div className="settings-group">
          <h4>Brand Voice</h4>
          <p className="settings-description">
            Configure how the AI crafts responses to match your brand personality.
          </p>

          <div className="settings-field">
            <label htmlFor="tone-select">Tone</label>
            <select
              id="tone-select"
              value={config.brandVoice.tone}
              onChange={(e) => setConfig(prev => ({
                ...prev,
                brandVoice: { ...prev.brandVoice, tone: e.target.value }
              }))}
            >
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="casual">Casual</option>
              <option value="warm">Warm</option>
              <option value="formal">Formal</option>
            </select>
          </div>

          <div className="settings-field">
            <label htmlFor="style-select">Style</label>
            <select
              id="style-select"
              value={config.brandVoice.style}
              onChange={(e) => setConfig(prev => ({
                ...prev,
                brandVoice: { ...prev.brandVoice, style: e.target.value }
              }))}
            >
              <option value="conversational">Conversational</option>
              <option value="empathetic">Empathetic</option>
              <option value="concise">Concise</option>
              <option value="detailed">Detailed</option>
              <option value="enthusiastic">Enthusiastic</option>
            </select>
          </div>

          <div className="settings-field">
            <label htmlFor="signoff-name">Sign-off Name</label>
            <input
              id="signoff-name"
              type="text"
              placeholder="e.g., The Management Team"
              value={config.brandVoice.signOffName}
              onChange={(e) => setConfig(prev => ({
                ...prev,
                brandVoice: { ...prev.brandVoice, signOffName: e.target.value }
              }))}
            />
          </div>
        </div>

        <div className="settings-group">
          <h4>Timing Overrides</h4>
          <p className="settings-description">
            Set how long after an order is completed before a review request is sent (in minutes).
          </p>

          <div className="timing-fields">
            <div className="settings-field">
              <label htmlFor="dine-in-delay">Dine-in Delay (minutes)</label>
              <input
                id="dine-in-delay"
                type="number"
                min="0"
                max="1440"
                value={config.timingOverrides.dineInDelay}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  timingOverrides: { ...prev.timingOverrides, dineInDelay: parseInt(e.target.value) || 0 }
                }))}
              />
            </div>

            <div className="settings-field">
              <label htmlFor="takeout-delay">Takeout Delay (minutes)</label>
              <input
                id="takeout-delay"
                type="number"
                min="0"
                max="1440"
                value={config.timingOverrides.takeoutDelay}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  timingOverrides: { ...prev.timingOverrides, takeoutDelay: parseInt(e.target.value) || 0 }
                }))}
              />
            </div>

            <div className="settings-field">
              <label htmlFor="delivery-delay">Delivery Delay (minutes)</label>
              <input
                id="delivery-delay"
                type="number"
                min="0"
                max="1440"
                value={config.timingOverrides.deliveryDelay}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  timingOverrides: { ...prev.timingOverrides, deliveryDelay: parseInt(e.target.value) || 0 }
                }))}
              />
            </div>
          </div>
        </div>

        <div className="settings-group">
          <h4>Notification Channels</h4>
          <p className="settings-description">
            Enable or disable review solicitation channels.
          </p>

          <div className="toggle-fields">
            <label className="toggle-field">
              <span className="toggle-label">
                <i className="fas fa-sms"></i>
                Enable SMS
              </span>
              <div
                className={`toggle-switch ${config.enableSms ? 'active' : ''}`}
                onClick={() => setConfig(prev => ({ ...prev, enableSms: !prev.enableSms }))}
                role="switch"
                aria-checked={config.enableSms}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setConfig(prev => ({ ...prev, enableSms: !prev.enableSms }));
                  }
                }}
              >
                <div className="toggle-knob"></div>
              </div>
            </label>

            <label className="toggle-field">
              <span className="toggle-label">
                <i className="fas fa-envelope"></i>
                Enable Email
              </span>
              <div
                className={`toggle-switch ${config.enableEmail ? 'active' : ''}`}
                onClick={() => setConfig(prev => ({ ...prev, enableEmail: !prev.enableEmail }))}
                role="switch"
                aria-checked={config.enableEmail}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setConfig(prev => ({ ...prev, enableEmail: !prev.enableEmail }));
                  }
                }}
              >
                <div className="toggle-knob"></div>
              </div>
            </label>
          </div>
        </div>

        <div className="settings-save-area">
          <button
            className="save-settings-btn"
            onClick={handleSaveConfig}
            disabled={savingConfig}
          >
            {savingConfig ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="review-management">
        <div className="review-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading review management for {storeName}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="review-management">
      {error && (
        <div className="review-alert review-alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>x</button>
        </div>
      )}

      {success && (
        <div className="review-alert review-alert-success">
          <i className="fas fa-check-circle"></i>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>x</button>
        </div>
      )}

      <div className="review-management-header">
        <h2>Review Management</h2>
        <p>Monitor reviews, manage AI responses, and solicit feedback for {storeName}</p>
      </div>

      <div className="review-nav">
        <button
          className={`review-nav-btn ${activeSection === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSection('overview')}
        >
          <i className="fas fa-chart-bar"></i>
          Overview
        </button>
        <button
          className={`review-nav-btn ${activeSection === 'reviews' ? 'active' : ''}`}
          onClick={() => setActiveSection('reviews')}
        >
          <i className="fas fa-comments"></i>
          Reviews
        </button>
        <button
          className={`review-nav-btn ${activeSection === 'solicitation' ? 'active' : ''}`}
          onClick={() => setActiveSection('solicitation')}
        >
          <i className="fas fa-bullhorn"></i>
          Solicitation
        </button>
        <button
          className={`review-nav-btn ${activeSection === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveSection('settings')}
        >
          <i className="fas fa-cog"></i>
          Settings
        </button>
      </div>

      <div className="review-management-content">
        {activeSection === 'overview' && renderOverview()}
        {activeSection === 'reviews' && renderReviews()}
        {activeSection === 'solicitation' && renderSolicitation()}
        {activeSection === 'settings' && renderSettings()}
      </div>
    </div>
  );
};
